import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpUrlEncodingCodec } from '@angular/common/http';
import { Observable } from 'rxjs/internal/Observable';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { ConfigService } from './config.service';
import { StorageProvider } from './storage.provider';
import { Multitenant } from './multitenant.provider';

@Injectable({ providedIn: 'root' })
export class ArticlesService {

  protected domain: string;
  protected client_id: string;
  protected client_secret: string;
  protected articlesRoute = '/articles';
  protected login = '/oauth/v2/token';
  private articleDetailRoute = '/article';
  private factSheetCategoriesRoute = '/coin-des-expose';
  private factSheetsRoute = '/fiches-expose';
  private factSheetDetailRoute = '/fiche-expose';
  private thematicFiles = '/dossiers-thematiques';
  private thematicFileDetail = '/dossiers-thematiques-pdf';
  private profiles = '/profils';
  private loginProfile = '/parent_login';
  private createAccount = '/user_new';
  private createProfile = '/profil_new';
  private sendEmailResetPassword = '/reset_send_email';
  private sendParentEmailResetPassword = '/parent_reset';
  private checkToken = '/reset_check_token';
  private checkTokenParent = '/reset_check_parent_token';
  private searchMysql = '/search';
  private resetFamilyPassword = '/reset_password';
  private resetParentPassword = '/parent_set_password';
  private archives = '/newspaper_pdf_list';
  private newspaperPdf = '/newspaper_pdf';
  private bookOfMonthList = '/books';
  private bookOfMonthDetail = '/book';
  private avatars = '/avatars';
  private saveProfile = '/profil_edit';
  private page = '/page';
  private pdf_download_counter = '/download_counter';
  private pubAdds = '/advertising';
  private casLogin = '/cas_login';
  private kneLogin = '/kne-connection';
  private checkPromoCode = '/check_promo_code';
  private addPromoCodeProfile = '/add_profil_promo_code';
  private unsubscribeEmailUrl = '/unsubscribe_clubdeslecteurs';
  private articlesCategories = '/articles_categories';
  private googleSignInCallback = '/google-signin-callback';
  protected encoder = new HttpUrlEncodingCodec();

  constructor(
    protected http: HttpClient,
    protected config: ConfigService,
    protected storage: StorageProvider,
    protected platform: Multitenant
  ) {
    this.getEnvConfig();
  }
  // Get domain by environment

  getEnvConfig() {
    const configData = this.config.getEnvironmentConfig();
    const environment = configData.env;
    this.domain = configData.environments[environment].domain;
    this.client_id = configData.environments[environment].client_id;
    this.client_secret = configData.environments[environment].client_secret;
  }

  // Fetch all existing comments
  getArticles(newspaper, category, start, limit, date = null): Observable<any> {
    const params: any = { start, limit };
    if (newspaper !== 'playbacpresse') {
      params.newspaper = newspaper;
      params.category = category;
      if (date) {
        params.date = date;
      }
    }

    if (this.storage.getStorage('access_token')) {
      params.profil = this.storage.getStorage('logged_profile_id');
      params.access_token = this.storage.getStorage('access_token');
    }

    return this.http.get(this.domain + this.articlesRoute, { params }).pipe( catchError((error) => throwError(error)) );
  }

  // Fetch article by slug
  getArticleDetailService(slug: string, category: string): Observable<any> {
    const params: any = { slug };
    const access = this.storage.getStorage('access');
    
    if (this.storage.getStorage('access_token') && category !== 'lecons-de-dessin-play' && !!access) {
      params.profil = this.storage.getStorage('logged_profile_id');
      params.access_token = this.storage.getStorage('access_token');
    }

    return this.http.get(this.domain + this.articleDetailRoute, { params }).pipe( catchError((error) => throwError(error)));
  }

  getFactSheetsCategories(newspaper): Observable<any> {
    return this.http.get(this.domain + this.factSheetCategoriesRoute + '?newspaper=' + newspaper)
      .pipe(catchError((error) => throwError(error)));
  }

  getFactSheets(slug): Observable<any> {
    const params: any = { };
    if (this.storage.getStorage('access_token')) {
      params.profil = this.storage.getStorage('logged_profile_id');
      params.access_token = this.storage.getStorage('access_token');
    }

    return this.http.get(this.domain + this.factSheetsRoute + '?subtheme=' + slug, { params })
    .pipe( catchError((error) => throwError(error)) );
  }

  getFactSheetDetail(slug): Observable<any> {
    const params: any = { slug };
    if (this.storage.getStorage('access_token')) {
      params.profil = this.storage.getStorage('logged_profile_id');
      params.access_token = this.storage.getStorage('access_token');
    }

    return this.http.get(this.domain + this.factSheetDetailRoute, { params }).pipe(catchError((error) => throwError(error)));
  }

  getThematicFilesCategories(newspaper): Observable<any> {
    return this.http.get(this.domain + this.thematicFiles + '?newspaper=' + newspaper).pipe( catchError((error) => throwError(error)));
  }

  getThematicFilesSubCategories(themeslug): Observable<any> {
    return this.http.get(this.domain + this.thematicFiles + '?theme_slug=' + themeslug).pipe(catchError((error) => throwError(error)));
  }

  getThematicFileDetail(subtheme): Observable<any> {
    return this.http.get(this.domain + this.thematicFileDetail + '?subtheme=' + subtheme)
      .pipe( catchError((error) => throwError(error)));
  }

  getJournalCategories(newspaper: any): Observable<any> {
    return this.http.get(this.domain + this.articlesCategories, { params: this.parseParameters({newspaper}) });
  }

  parseParameters(parameters: Object = {}): HttpParams {
    let requestParameters = new HttpParams({encoder: this.encoder});
    Object.keys(parameters).forEach(key => {
      if (parameters[key]) {
        requestParameters = requestParameters.set(key, parameters[key]);
      }
    });

    return requestParameters;
  }

  loginToPlatform(model): Observable<any> {
    const body = JSON.stringify(model); // Stringify payload
    const headers = new HttpHeaders(); // ... Set content type to JSON
    headers.append('Content-Type', 'application/x-www-form-urlencoded');

    return this.http.post(this.domain + this.login, body,  { headers }).pipe(catchError((error) =>  throwError(error)));
  }

  loginWithGoogle(googleUser: any): Observable<any> {
    const payload = { id: googleUser.sub, email: googleUser.email, firstName: googleUser.given_name, lastName: googleUser.family_name };
    return this.http.post<any>(this.domain + this.googleSignInCallback, this.parseParameters(payload));
  }
  
  loginToPlatformGet(username, password): Observable<any> {
    let language = '';
    if (this.platform.getDomainClass() === 'mwjp') {
      language = '&specific_language=mwjp';
    }
    if (this.platform.getDomainClass() === 'mwde') {
      language = '&specific_language=mwde';
    }
    if (this.platform.getDomainClass() === 'clfy') {
      language = '&specific_language=clfy';
    }

    return this.http
      .get(
        this.domain + this.login + '?client_id=' + this.client_id + '&client_secret=' + this.client_secret +
          '&grant_type=password&password=' + encodeURIComponent(password) + '&username=' + username + language
      )
      .pipe(catchError((error) => throwError(error.json())));
  }

  getUserProfiles(access_token): Observable<any> {

    const uniq = localStorage.getItem('ID');
    return this.http
      .get( this.domain + this.profiles + '?access_token=' + access_token + '&device_identifier=' + uniq )
      .pipe(catchError((error) => throwError(error)));
  }

  authenticateProfile(model, access_token): Observable<any> {
    const body = JSON.stringify(model); // Stringify payload
    const headers = new HttpHeaders(); // ... Set content type to JSON
    headers.append('Content-Type', 'application/x-www-form-urlencoded');
    return this.http
      .post( this.domain + this.loginProfile + '?access_token=' + access_token + '&password=' + model.password, body, { headers } )
      .pipe( catchError((error) => throwError(error)) );
  }

  createAccountPost(model): Observable<any> {
    const body = JSON.stringify(model); // Stringify payload
    const headers = new HttpHeaders(); // ... Set content type to JSON
    headers.append('Content-Type', 'application/x-www-form-urlencoded');

    return this.http.post(this.domain + this.createAccount, body, { headers }).pipe(catchError((error) => throwError(error)));
  }

  createProfilePost(model, access_token): Observable<any> {
    model.access_token = access_token;
    model.type === 'Parent' ? (model.password = 'catoRTiOusta') : '';
    const body = JSON.stringify(model); // Stringify payload
    const headers = new HttpHeaders(); // ... Set content type to JSON
    headers.append('Content-Type', 'application/x-www-form-urlencoded');

    return this.http.post(this.domain + this.createProfile, body, { headers }).pipe(catchError((error) => throwError(error.json())));
  }

  forgottenPassword(username): Observable<any> {
    return this.http
      .get(this.domain + this.sendEmailResetPassword + '?username=' + username)
      .pipe(catchError((error) => throwError(error)));
  }

  forgottenPasswordParent(): Observable<any> {
    const params: HttpParams = new HttpParams();
    if (this.storage.getStorage('access_token')) {
      params.set('access_token', this.storage.getStorage('access_token'));
    }
    const headers = new HttpHeaders(); // ... Set content type to JSON
    headers.append('Content-Type', 'application/x-www-form-urlencoded');

    return this.http
      .post(this.domain + this.sendParentEmailResetPassword, params, { headers }).pipe(catchError((error) => throwError(error)));
  }

  isTokenValid(token): Observable<any> {
    return this.http.get(this.domain + this.checkToken + '?token=' + token).pipe(catchError((error) => throwError(error)));
  }

  isTokenValidParent(token): Observable<any> {
    return this.http.get(this.domain + this.checkTokenParent + '?token=' + token).pipe(catchError((error) => throwError(error)));
  }

  resetPassword(model): Observable<any> {
    const body = JSON.stringify(model); // Stringify payload
    const headers = new HttpHeaders(); // ... Set content type to JSON
    headers.append('Content-Type', 'application/x-www-form-urlencoded');

    return this.http.post(this.domain + this.resetFamilyPassword, body, { headers }).pipe(catchError((error) => throwError(error)));
  }

  resetParentPass(model): Observable<any> {
    const body = JSON.stringify(model); // Stringify payload
    const headers = new HttpHeaders(); // ... Set content type to JSON
    headers.append('Content-Type', 'application/x-www-form-urlencoded');

    return this.http.post(this.domain + this.resetParentPassword, body, { headers }).pipe(catchError((error) => throwError(error)));
  }

  getSearchResults(search_string, newspaper, start, limit): Observable<any> {
    const params: any = { search: search_string, start, limit};
    if (newspaper !== 'playbacpresse') {
      params.newspaper = newspaper;
    }
    if (this.storage.getStorage('access_token')) {
      params.profil = this.storage.getStorage('logged_profile_id');
      params.access_token = this.storage.getStorage('access_token');
    }
    return this.http.get(this.domain + this.searchMysql, { params }).pipe(catchError((error) => throwError(error)));
  }

  getArchivesByNewspaper(newspaper, start, limit): Observable<any> {
    const params: any = { newspaperSubname: newspaper, start, limit };
    if (this.storage.getStorage('access_token')) {
      params.profil = this.storage.getStorage('logged_profile_id');
      params.access_token = this.storage.getStorage('access_token');
    }

    return this.http.get(this.domain + this.archives, { params }).pipe(catchError((error) => throwError(error)));
  }

  getPDFByNewspaperAndDate(newspaper: string, date:string): Observable<any> {
    const params: any = { newspaperSubname: newspaper, newspaperPdfDate: date };
    if (this.storage.getStorage('access_token')) {
      params.profil = this.storage.getStorage('logged_profile_id');
      params.access_token = this.storage.getStorage('access_token');
    }

    return this.http.get(this.domain + this.newspaperPdf, { params }).pipe(catchError((error) => throwError(error)));
  }

  getBookOfMonthList(newspaper): Observable<any> {
    const params = { newspaper };
    return this.http.get(this.domain + this.bookOfMonthList, { params }).pipe(catchError((error) => throwError(error)));
  }

  getBookDetail(slug): Observable<any> {
    const params: any = { slug };
    if (this.storage.getStorage('access_token')) {
      params.profil = this.storage.getStorage('logged_profile_id');
      params.access_token = this.storage.getStorage('access_token');
    }

    return this.http.get(this.domain + this.bookOfMonthDetail, { params }).pipe(catchError((error) => throwError(error)));
  }

  getAvatars(): Observable<any> {
    return this.http.get(this.domain + this.avatars).pipe(catchError((error) => throwError(error)));
  }

  saveProfileGet(model, change): Observable<any> {
    const params: any = { nick_name: model.nickname, profil: model.profil_id, avatar: model.avatar_id };

    if (change) {
      params.lastname = model.lastName;
      params.subscriber_number = model.subscriber_number;
    }
    if (this.storage.getStorage('access_token')) {
      params.access_token = this.storage.getStorage('access_token');
    }

    return this.http.get(this.domain + this.saveProfile, { params }).pipe(catchError((error) => throwError(error)));
  }

  getPage(page, newspaper): Observable<any> {
    const params: any = { slug: page };
    if (newspaper && newspaper !== 'playbacpresse') {
      params.newspaper = newspaper;
    }

    return this.http.get(this.domain + this.page, { params }).pipe(catchError((error) => throwError(error)));
  }

  pdfDownloadCounter(mediaId): Observable<any> {
    const params = { mediaId };

    return this.http.get(this.domain + this.pdf_download_counter, { params }).pipe(catchError((error) => throwError(error)));
  }

  getPubAdds(type, newspaper): Observable<any> {
    const params: any = { type, newspaper };

    if (this.storage.getStorage('access_token')) {
      params.access_token = this.storage.getStorage('access_token');
      params.profil = this.storage.getStorage('logged_profile_id');
    }

    return this.http.get(this.domain + this.pubAdds, { params }).pipe(catchError((error) => throwError(error)));
  }

  getCasLogin(): Observable<any> {
    return this.http.get(this.domain + this.casLogin).pipe(catchError((error) => throwError(error)));
  }

  getKneLogin(params: { GUIDUser: string, GUIDRessource: string }) {
    return this.http.get(this.domain + this.kneLogin, { params }).pipe(catchError((error) => throwError(error)));
  }

  checkPormoCode(coupon: string): Observable<any> {
    const params: any = { coupon };

    if (this.storage.getStorage('access_token')) {
      params.access_token = this.storage.getStorage('access_token');
    }
    return this.http.get(this.domain + this.checkPromoCode, { params }).pipe(catchError((error) => throwError(error)));
  }

  addPromoCode(coupon: string, profile: string, access_token: string): Observable<any> {
    const params: any = { coupon, profil: profile, access_token };

    return this.http.get(this.domain + this.addPromoCodeProfile, { params }).pipe(catchError((error) => throwError(error)));
  }

  getEmailUnsubscribe(email: string, journal: string, codeClient: string, profileId: string): Observable<any> {
    const params = { email, journal, codeClient, profileId };

    return this.http.get(this.domain + this.unsubscribeEmailUrl, { params }).pipe(catchError((error) => throwError(error)));
  }

  getCalameoUrlForIframe(url: string): Observable<{ link: string }> {
    return this.http.get<{ link: string }>(url);
  }
}
